import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AvailableAccount } from "src/app/accounts/AvailableAccount";
import { UserDTO } from "src/app/auth/User";
import { NotificationService } from "src/app/notifications/notification.service";
import { PermissionService } from "src/app/permission.service";
import { SessionService } from "src/app/session.service";
import { AreYouSureDialogComponent } from "src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component";
import { TitleService } from "src/app/title.service";
import { UiService } from "src/app/ui/ui.service";
import { UsersService } from "src/app/user/users.service";
import { RoleDTO } from "../Role";

const permissionGroupRegex = /[^.]+/;

@Component({
  selector: "app-role",
  templateUrl: "./role.component.html",
  styleUrls: ["./role.component.scss"],
})
export class RoleComponent implements OnInit {
  role!: RoleDTO;
  newRole!: boolean;
  newRoleForm?: FormGroup;
  editRoleForm?: FormGroup;
  roleOptions?: RoleDTO[];
  permissionGroups: any;
  account!: AvailableAccount;
  showDelete!: boolean;
  notShownPermissions: any[] = [];
  private onComponentDestroy = new Subject();

  constructor(
    private activatedRoute: ActivatedRoute,
    private TitleService: TitleService,
    private router: Router,
    private PermissionService: PermissionService,
    private SessionSerive: SessionService,
    private NotificationService: NotificationService,
    private Dialog: MatDialog,
    private UserService: UsersService,
    private ChangeDetectorRef: ChangeDetectorRef,
    private uiService: UiService,
  ) { }

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(() => {
        this.role = this.activatedRoute.snapshot.data.role;
        this.account = this.SessionSerive.currentAccountValue!;

        this.newRole = !this.role;
        if (!this.newRole) {
          this.TitleService.setTitle(
            this.TitleService.getTitle() + ` | ${this.role.name}`,
          );
        }

        const copyRole = this.activatedRoute.snapshot.data.copyRole;
        //If copy role was selected, set that copy role as the new role
        if (copyRole) {
          this.role = copyRole;
          this.role.name = `${this.role.name} copy`;
        }

        this.initiateForms();
        this.initiatePermissions(this.activatedRoute.snapshot.data.permissions);
      });

    Promise.all([this.PermissionService.categories.roles.hasRemove()]).then(
      ([hasRemove]) => {
        this.showDelete = hasRemove;
      },
    );
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get currentAccount() {
    return this.SessionSerive.currentAccountValue;
  }

  get currentUser() {
    return this.SessionSerive.currentUserValue;
  }

  initiatePermissions(permissions: any) {
    this.permissionGroups = [
      {
        code: "accounts",
        name: "Accounts",
        permissions: [],
        selectAll: false,
      },
      {
        code: "accountApps",
        name: "Apps",
        permissions: [],
        selectAll: false,
      },
      {
        code: "accountAppUser",
        name: "Customers",
        permissions: [],
        selectAll: false,
      },
      {
        code: "beaconsGeofences",
        name: "Beacons and Geofences",
        permissions: [],
        selectAll: false,
      },
      {
        code: "signals",
        name: "Signals",
        permissions: [],
        selectAll: false,
      },
      {
        code: "facilities",
        name: "Facilities",
        permissions: [],
        selectAll: false,
      },
      {
        code: "logicTrees",
        name: "Logic Trees",
        permissions: [],
        selectAll: false,
      },
      {
        code: "users",
        name: "Users",
        permissions: [],
        selectAll: false,
      },
      {
        code: "media",
        name: "Media",
        permissions: [],
        selectAll: false,
      },
      {
        code: "webhooks",
        name: "Webhooks",
        permissions: [],
        selectAll: false,
      },
      {
        code: "integrations",
        name: "Integrations",
        permissions: [],
        selectAll: false,
      },
      {
        code: "assistist",
        name: "Assistist",
        permissions: [],
        selectAll: false,
      },
      {
        code: "luma",
        name: "Luma",
        permissions: [],
        selectAll: false,
      },
    ];

    const customersGroup = this.permissionGroups.find(
      (group: any) => group.code === "accountAppUser",
    );
    const appsGroup = this.permissionGroups.find(
      (group: any) => group.code === "accountApps",
    );
    const accountsGroup = this.permissionGroups.find(
      (group: any) => group.code === "accounts",
    );
    const webhooksGroup = this.permissionGroups.find(
      (group: any) => group.code === "webhooks",
    );
    const beaconsGeofencesGroup = this.permissionGroups.find(
      (group: any) => group.code === "beaconsGeofences",
    );
    const facilitiesGroup = this.permissionGroups.find(
      (group: any) => group.code === "facilities",
    );
    const logicTreesGroup = this.permissionGroups.find(
      (group: any) => group.code === "logicTrees",
    );
    const mediaGroup = this.permissionGroups.find(
      (group: any) => group.code === "media",
    );
    const signalsGroup = this.permissionGroups.find(
      (group: any) => group.code === "signals",
    );
    const usersGroup = this.permissionGroups.find(
      (group: any) => group.code === "users",
    );
    const integrationsGroup = this.permissionGroups.find(
      (group: any) => group.code === "integrations",
    );
    const assististGroup = this.permissionGroups.find((group: any) => group.code === "assistist");
    const lumaGroup = this.permissionGroups.find((group: any) => group.code === "luma");

    for (let permission of permissions) {
      const groupCode = permission.code.match(permissionGroupRegex)[0];

      permission.selected = !!this.role?.permissions.find(
        (rolePerm) => rolePerm.code === permission.code,
      );
      //
      if (groupCode === "accountAppUser") {
        customersGroup!.permissions.push(permission);
      
      //
      } else if (
        groupCode === "account_user_groups" ||
        groupCode === "account_users" ||
        groupCode === "accounts"
      ) {
        accountsGroup!.permissions.push(permission);

      //
      } else if (groupCode === "accountApps") {
        appsGroup!.permissions.push(permission);

      //
      } else if (groupCode === "apiWebhooks") {
        webhooksGroup!.permissions.push(permission);

      //
      } else if (
        groupCode === "beaconRegions" ||
        groupCode === "beacons" ||
        groupCode === "geofences"
      ) {
        beaconsGeofencesGroup!.permissions.push(permission);

      //
      } else if (groupCode === "facilities") {
        facilitiesGroup!.permissions.push(permission);

      //
      } else if (groupCode === "logicTrees") {
        logicTreesGroup!.permissions.push(permission);

      //
      } else if (groupCode === "media") {
        mediaGroup!.permissions.push(permission);

      //
      } else if (
        groupCode === "quicksignal" ||
        groupCode === "signal-template" ||
        groupCode === "account-signal-category"
      ) {
        signalsGroup!.permissions.push(permission);

      //
      } else if (groupCode === "userNotification" || groupCode === "users") {
        usersGroup!.permissions.push(permission);
      } else if (groupCode === "integrations") {
        integrationsGroup!.permissions.push(permission);
      
      //
      } else if (groupCode === "documents" || groupCode === "document_categories") {
        assististGroup!.permissions.push(permission);
      } else if (groupCode === "luma") {
        lumaGroup!.permissions.push(permission);
      } else {
        //Group permissions not displayed on screen to not overwrite
        this.notShownPermissions.push(permission);
      }
    }

    for (let group of this.permissionGroups) {
      this.checkSelectAll(group);
    }
  }

  initiateForms() {
    if (this.newRole) {
      this.newRoleForm = new FormGroup({
        roleName: new FormControl(this.role?.name, [Validators.required]),
        roleDesc: new FormControl(this.role?.description, Validators.required),
      });
    } else {
      this.editRoleForm = new FormGroup({
        roleName: new FormControl(this.role.name, [Validators.required]),
        role: new FormControl(this.role.code, [Validators.required]),
        roleDesc: new FormControl(this.role.description, Validators.required),
      });
      this.roleOptions = this.activatedRoute.snapshot.data.roles.filter(
        (role: RoleDTO) => {
          return this.SessionSerive.isSuper || !!role.account_id;
        },
      );
    }
  }

  onSelectAll(group: any) {
    if (group.selectAll) {
      for (let permission of group.permissions) {
        permission.selected = true;
      }
    } else {
      for (let permission of group.permissions) {
        permission.selected = false;
      }
    }
  }

  checkSelectAll(group: any) {
    let selectAll = true;
    for (let permission of group.permissions) {
      if (!permission.selected) {
        selectAll = false;
        break;
      }
    }

    group.selectAll = selectAll;

    this.ChangeDetectorRef.detectChanges();
  }

  onPermissionSelected(permission: any, group: any) {
    // permission.selected = !permission.selected;
    this.checkSelectAll(group);
  }

  deleteRole() {
    this.Dialog.open(AreYouSureDialogComponent, {
      data: {
        areYouSureInfo: {
          description: `Are you sure you want to delete this role ( ${this.role.name} )?`,
          warning: "Warning: this is not reversible!",
        },
      },
    })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.uiService
            .waitForEvent(
              this.PermissionService.deleteRole(
                this.currentAccount!.accountID,
                this.role.id,
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              () => {
                this.NotificationService.openSuccessSnackBar("Role deleted");
                this.router.navigate(["list"], {
                  relativeTo: this.activatedRoute.parent,
                });
              },
              () => {
                this.NotificationService.openErrorSnackBar(
                  "Error deleting role",
                );
              },
            );
        }
      });
  }

  onRoleChange(event: any) {
    const roleID = this.roleOptions?.find((option: any) => {
      return option.code === event.value;
    })!.id;

    this.router.navigate([roleID], { relativeTo: this.activatedRoute.parent });
  }

  get selectedPermissions() {
    const selectedPermissions = [];
    for (let group of this.permissionGroups) {
      for (let permission of group.permissions) {
        if (permission.selected) {
          selectedPermissions.push({
            code: permission.code,
            description: permission.description,
            id: permission.id,
            name: permission.name,
          });
        }
      }
    }
    //Add not shown permissions to not overwrite
    for (let permission of this.notShownPermissions) {
      if (permission.selected) {
        selectedPermissions.push({
          code: permission.code,
          description: permission.description,
          id: permission.id,
          name: permission.name,
        });
      }
    }
    return selectedPermissions;
  }

  saveNewRole() {
    const roleDTO = {
      code: this.getRoleCode(),
      description: this.newRoleForm!.controls.roleDesc.value,
      name: this.newRoleForm!.controls.roleName.value,
      permissions: this.selectedPermissions,
      account_id: this.account.accountID,
    };

    this.uiService
      .waitForEvent(
        this.PermissionService.createRole(
          this.currentAccount!.accountID,
          roleDTO as RoleDTO,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (res) => {
          this.uiService
            .waitForEvent(
              this.UserService.getUser(
                this.currentAccount!.accountID,
                this.currentUser!.id,
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe((user) => {
              this.SessionSerive.updateUserRoles(user as unknown as UserDTO);
              this.router.navigate(["list"], {
                relativeTo: this.activatedRoute.parent,
              });
            });

          this.NotificationService.openSuccessSnackBar(
            "Role Updated Succesfully",
          );
        },
        (err) => {
          if (err.error.error.constraint === "roles_code_key") {
            this.NotificationService.openErrorSnackBar(
              "Role name already in use",
            );
          } else {
            this.NotificationService.openErrorSnackBar("Error Creating Role");
          }
        },
      );
  }

  saveRoleUpdates() {
    const roleDTO = {
      code: this.getRoleCode(),
      description: this.editRoleForm?.controls.roleDesc.value,
      name: this.editRoleForm?.controls.roleName.value,
      id: this.role.id,
      rank: this.role.rank,
      permissions: this.selectedPermissions,
    };

    this.uiService
      .waitForEvent(
        this.PermissionService.updateRole(
          this.currentAccount!.accountID,
          roleDTO.id,
          roleDTO as RoleDTO,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (res) => {
          this.uiService
            .waitForEvent(
              this.UserService.getUser(
                this.currentAccount!.accountID,
                this.currentUser!.id,
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe((user) => {
              console.log(user);
              this.SessionSerive.updateUserRoles(user as unknown as UserDTO);
              this.router.navigate(["list"], {
                relativeTo: this.activatedRoute.parent,
              });
            });
          this.NotificationService.openSuccessSnackBar(
            "Role Updated Succesfully",
          );
        },
        (err) => {
          if (err.error.error.constraint === "roles_code_key") {
            this.NotificationService.openErrorSnackBar(
              "Role name already in use",
            );
          } else {
            this.NotificationService.openErrorSnackBar("Error Updating Role");
          }
        },
      );
  }

  getRoleCode() {
    if (!this.newRole) {
      return `${this.formatCode(
        this.editRoleForm?.controls.roleName.value,
      )}`;
    } else {
      return `${this.formatCode(
        this.newRoleForm?.controls.roleName.value,
      )}`;
    }
  }

  formatCode(value: string) {
    let returnString = "";
    value = value.toLowerCase();
    for (let i = 0; i < value.length; i++) {
      if (value.charAt(i) === " ") {
        returnString = returnString + "_";
      } else {
        returnString = returnString + value.charAt(i);
      }
    }

    return returnString;
  }

  allPermissionsSelected(group: any) {
    for (let permission of group.permissions) {
      if (!permission.selected) {
        return false;
      }
    }
    return true;
  }

  cancel() {
    this.router.navigate(["list"], { relativeTo: this.activatedRoute.parent });
  }
}
